import React from 'react';
import tw from 'twin.macro';
import Helmet from 'react-helmet';
import Bugsnag from '@bugsnag/js';
import { css } from '@emotion/react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import isMobile, { isDevice } from '$/utils/isMobile';
import { setUseWhatChange } from '@simbathesailor/use-what-changed';
import { ServerContext, useServerDateNow } from '$/hooks/useServerDateNow';
import BugsnagPluginReact, { BugsnagErrorBoundary } from '@bugsnag/plugin-react';

import config from '$/config';

import AccessChecker from '../Redirects';
import { WithToast } from '../Toast/Toast';
import { ErrorBoundaryView } from '../pages/404/ErrorBoundaryView';
import { DirtyFormContextProvider } from '../Router/DirtyFormContext';
import { EWebinarThemeProvider } from '../ui/ThemeContext';

interface LayoutProps {
  className?: string;
  noAuth?: boolean;
  audience?: boolean;
  children?: React.ReactNode;
}

const setRetry = (key: string, value: string, interval: number): void => {
  const item = {
    value: value,
    delay: new Date(Date.now()).getTime() + interval,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

const canRetry = (key: string): boolean => {
  const itemString = window.localStorage.getItem(key);
  if (!itemString) return true;

  const item = JSON.parse(itemString);
  const isExpiredDelay = new Date(Date.now()).getTime() > item.delay;

  if (isExpiredDelay) {
    localStorage.removeItem(key);
    return true;
  }

  return false;
};

let ErrorBoundary: BugsnagErrorBoundary = ({ children }) => <>{children}</>;
const bugsnagKey = config.GATSBY_BUGSNAG_KEY;
if (bugsnagKey) {
  const skipErrors = [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
    `Cannot find module '../locales/en/messages.js'`,
    `Object Not Found Matching Id`,
  ];

  Bugsnag.start({
    apiKey: bugsnagKey,
    enabledReleaseStages: ['production', 'test', 'development'],
    logger: null,
    plugins: [new BugsnagPluginReact()],
    releaseStage: config.bugsnagEnv,
    appVersion: process.env.DEPLOY_ID,
    onError: function (event): void | boolean {
      const err = event.errors[0];
      if (skipErrors.some((message) => err.errorMessage.includes(message))) {
        return false;
      }

      // TODO: handle loading chunk failed
      const chunkFailedRegex = /Loading chunk [\d]+ failed/;
      if (chunkFailedRegex.test(err.errorMessage)) {
        if (canRetry('chunk_failed')) {
          setRetry('chunk_failed', 'true', 10000);
          window.location.reload();
          return false;
        }
      }
    },
  });

  ErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary(React);
}

const Layout: React.FC<LayoutProps> = ({ children, className, noAuth, audience }) => {
  setUseWhatChange(process.env.NODE_ENV === 'local');

  const [serverDate, setServerDate] = React.useState(new Date());
  const serverDateNow = useServerDateNow(serverDate);

  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryView}>
      <Helmet async>
        <meta name='apple-mobile-web-app-capable' content='yes' />
        <meta
          name='viewport'
          content={`${
            !isMobile && isDevice
              ? 'width=750, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
              : 'minimal-ui, width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, shrink-to-fit=no'
          }`}
        />
        <link
          fetchPriority={audience ? 'low' : undefined}
          rel='stylesheet'
          href='//cdn.jsdelivr.net/npm/semantic-ui@2.4.1/dist/semantic.min.css'
        />
        <link
          fetchPriority={audience ? 'low' : undefined}
          rel='stylesheet'
          href='https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;1,400;1,600&family=DM+Mono&family=Merriweather:wght@400;700&display=swap'
        />
        F
      </Helmet>
      {/* audience == registrant/attendee pages */}
      {!audience && (
        <Helmet async>
          {/* Need for browsers not support scroll-behavior : smooth (safari) */}
          {/* <!-- Any polyfill to enable smoothscroll for the JavaScript APIs --> */}
          <script src='https://unpkg.com/smoothscroll-polyfill/dist/smoothscroll.min.js'></script>
          {/* <!-- This package, to apply the smoothscroll to anchor links --> */}
          <script src='https://unpkg.com/smoothscroll-anchor-polyfill'></script>
        </Helmet>
      )}
      <WithToast>
        <main
          className={className}
          css={css`
            @media screen and (min-width: 641px) {
              min-height: 100vh;
            }
            .wide-container,
            .page {
              @media screen and (max-width: 640px) {
                padding-left: 1rem;
                padding-right: 1rem;
              }
              @media screen and (min-width: 641px) {
                min-width: 641px;
                max-width: calc(100% - 2.5rem);
                margin-left: auto;
                margin-right: auto;
              }
              @media screen and (min-width: 920px) {
                min-width: 920px;
                max-width: calc(100% - 12.5rem);
                margin-left: auto;
                margin-right: auto;
              }
              @media screen and (min-width: 1380px) {
                max-width: calc(1380px - 12.5rem);
              }
            }
            & .wide-page {
              @media screen and (max-width: 640px) {
                max-width: 100%;
              }
              @media screen and (min-width: 641px) {
                max-width: calc(100% - 2rem);
                margin-right: 1rem;
                margin-left: 1rem;
              }
              ${tw`bg-white shadow-lg`}
            }
            @media screen and (max-width: 767px) {
              .navbar-header {
                transform: scale(0.83);
                transform-origin: bottom left;
              }
              .container {
                ${tw`px-1/2e`}
              }
              .reg-sections {
                .container {
                  ${tw`px-0`}
                }
              }
            }
          `}
        >
          <ServerContext.Provider
            value={{
              serverDateNow,
              setServerDate,
            }}
          >
            <DirtyFormContextProvider>
              <GoogleOAuthProvider clientId={config.GOOGLE_AUTH_CLIENT_ID}>
                <AccessChecker noAuth={Boolean(noAuth)}>
                  <EWebinarThemeProvider>{children}</EWebinarThemeProvider>
                </AccessChecker>
              </GoogleOAuthProvider>
            </DirtyFormContextProvider>
          </ServerContext.Provider>
        </main>
      </WithToast>
      <div id='component-dimension-portal' className='invisible' />
    </ErrorBoundary>
  );
};

export default Layout;
